import {
  addDays,
  addMinutes,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { windowsToIana } from '../config/timeZones'
import type {
  AvailabilityItem,
  AvailableSlot,
  BookingBusinessHours,
  BookingBusinessInfo,
  BookingQuestionDefinition,
  BookingService,
  BookingStaffMember,
  CustomerDetails,
  DateTimeTimeZone,
  SchedulingPolicy,
} from '../types/bookings'

export function parseDurationToMinutes(duration: string | null | undefined): number {
  if (!duration) {
    return 30
  }

  const match = duration.match(
    /^P(?:(\d+(?:\.\d+)?)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/,
  )
  if (!match) {
    return 30
  }

  const days = Number(match[1] ?? 0)
  const hours = Number(match[2] ?? 0)
  const minutes = Number(match[3] ?? 0)
  const seconds = Number(match[4] ?? 0)
  const total = days * 24 * 60 + hours * 60 + minutes + seconds / 60

  return total > 0 ? total : 30
}

export function formatDuration(duration: string | null | undefined): string {
  const minutes = parseDurationToMinutes(duration)
  const hours = Math.floor(minutes / 60)
  const remainder = minutes % 60

  if (hours && remainder) {
    return `${hours}h ${remainder}m`
  }

  if (hours) {
    return `${hours}h`
  }

  return `${minutes}m`
}

export function formatSlotLabel(date: Date, timeZone: string): string {
  return formatInTimeZone(date, windowsToIana(timeZone), 'HH:mm')
}

export function normalizeCustomerPhone(phone: string): string {
  const digitsOnly = phone.replace(/\D/g, '')
  return digitsOnly || phone.trim()
}

export function toDateTimeTimeZone(date: Date, timeZone: string): DateTimeTimeZone {
  return {
    dateTime: formatInTimeZone(date, windowsToIana(timeZone), "yyyy-MM-dd'T'HH:mm:ss"),
    timeZone,
  }
}

export function createDayRange(date: Date, timeZone: string): {
  startDateTime: DateTimeTimeZone
  endDateTime: DateTimeTimeZone
} {
  const ianaId = windowsToIana(timeZone)
  const dateStr = format(date, 'yyyy-MM-dd')
  const startUtc = fromZonedTime(`${dateStr}T00:00:00`, ianaId)
  const endUtc = fromZonedTime(`${dateStr}T23:59:59`, ianaId)
  return {
    startDateTime: toDateTimeTimeZone(startUtc, timeZone),
    endDateTime: toDateTimeTimeZone(endUtc, timeZone),
  }
}

export function buildCalendarDays(month: Date): Date[] {
  const monthStart = startOfMonth(month)
  const monthEnd = endOfMonth(monthStart)
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 })
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 })
  const days: Date[] = []

  let cursor = calendarStart
  while (cursor <= calendarEnd) {
    days.push(cursor)
    cursor = addDays(cursor, 1)
  }

  return days
}

export function isPastDay(date: Date): boolean {
  return startOfDay(date) < startOfDay(new Date())
}

export function isDaySelectable(
  date: Date,
  month: Date,
  maxDate: Date,
  openDays?: Set<number>,
): boolean {
  if (isPastDay(date)) return false
  if (!isSameMonth(date, month)) return false
  if (date > maxDate) return false
  if (openDays && openDays.size > 0 && !openDays.has(date.getDay())) return false
  return true
}

const DAY_NAMES = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
] as const

export function getBusinessOpenDays(businessHours: BookingBusinessHours[]): Set<number> {
  const open = new Set<number>()
  businessHours.forEach((entry) => {
    if (entry.timeSlots.length > 0) {
      const idx = DAY_NAMES.indexOf(entry.day)
      if (idx >= 0) open.add(idx)
    }
  })
  return open
}

export function clipSlotsToHours(
  slots: AvailableSlot[],
  hours: BookingBusinessHours[],
  timeZone: string,
): AvailableSlot[] {
  const ianaId = windowsToIana(timeZone)

  return slots.filter((slot) => {
    const slotDayName = formatInTimeZone(slot.startDateTime, ianaId, 'EEEE').toLowerCase()
    const hoursForDay = hours.find((h) => h.day === slotDayName)
    if (!hoursForDay || hoursForDay.timeSlots.length === 0) return false

    const slotDateStr = formatInTimeZone(slot.startDateTime, ianaId, 'yyyy-MM-dd')

    return hoursForDay.timeSlots.some((ts) => {
      const startTime = ts.startTime.substring(0, 8)
      const endTime = ts.endTime.substring(0, 8)
      const open = fromZonedTime(`${slotDateStr}T${startTime}`, ianaId)
      const close = fromZonedTime(`${slotDateStr}T${endTime}`, ianaId)
      return slot.startDateTime >= open && slot.startDateTime <= close
    })
  })
}

export function clipSlotsToBusinessHours(
  slots: AvailableSlot[],
  businessInfo: BookingBusinessInfo,
): AvailableSlot[] {
  return clipSlotsToHours(slots, businessInfo.businessHours, businessInfo.timeZone)
}

export function describeQuestion(question: BookingQuestionDefinition): string {
  return question.isRequired ? `${question.displayName} *` : question.displayName
}

export function getMaximumAdvanceDate(policy?: SchedulingPolicy | null): Date {
  const advanceMinutes = parseDurationToMinutes(policy?.maximumAdvance ?? 'PT720H')
  const days = Math.max(Math.floor(advanceMinutes / (24 * 60)), 30)
  return addDays(new Date(), days)
}

export function generateSlotsFromAvailability(params: {
  availability: AvailabilityItem[]
  service: BookingService
  staff: BookingStaffMember
  timeZone: string
}): AvailableSlot[] {
  const slotMinutes = parseDurationToMinutes(params.service.duration)
  const ianaId = windowsToIana(params.timeZone)
  const slots: AvailableSlot[] = []

  params.availability.forEach((item) => {
    if (item.status !== 'available' && item.status !== 'slotsAvailable') {
      return
    }

    const startTzIana = windowsToIana(item.startDateTime.timeZone, ianaId)
    const endTzIana = windowsToIana(item.endDateTime.timeZone, ianaId)
    let cursor = fromZonedTime(item.startDateTime.dateTime, startTzIana)
    const windowEnd = fromZonedTime(item.endDateTime.dateTime, endTzIana)

    while (cursor <= windowEnd) {
      const startDateTime = new Date(cursor)
      const endDateTime = addMinutes(startDateTime, slotMinutes)

      slots.push({
        staffId: params.staff.id,
        staffName: params.staff.displayName,
        startDateTime,
        endDateTime,
        label: formatSlotLabel(startDateTime, params.timeZone),
      })

      cursor = addMinutes(cursor, slotMinutes)
    }
  })

  return slots
}

export function dedupeSlots(slots: AvailableSlot[]): AvailableSlot[] {
  const seen = new Set<string>()

  return slots.filter((slot) => {
    const key = slot.startDateTime.toISOString()
    if (seen.has(key)) {
      return false
    }

    seen.add(key)
    return true
  })
}

export function sortSlots(slots: AvailableSlot[]): AvailableSlot[] {
  return [...slots].sort(
    (left, right) => left.startDateTime.getTime() - right.startDateTime.getTime(),
  )
}

export function isSlotSelected(slot: AvailableSlot, selectedSlot: AvailableSlot | null): boolean {
  if (!selectedSlot) {
    return false
  }

  return slot.startDateTime.toISOString() === selectedSlot.startDateTime.toISOString()
}

export function formatAppointmentDate(date: Date): string {
  return format(date, 'EEEE, MMMM d')
}

export function formatAppointmentDateTime(start: Date, end: Date, timeZone: string): string {
  const ianaId = windowsToIana(timeZone)
  return `${formatInTimeZone(start, ianaId, 'EEE, MMM d')} • ${formatInTimeZone(start, ianaId, 'HH:mm')} - ${formatInTimeZone(end, ianaId, 'HH:mm')}`
}

export function isSameCalendarDay(left: Date | null, right: Date): boolean {
  return Boolean(left && isSameDay(left, right))
}

export function getServiceQuestions(service: BookingService | null): BookingQuestionDefinition[] {
  return service?.customQuestions ?? []
}

export function hasSmsSupport(service: BookingService | null): boolean {
  return Boolean(service?.smsNotificationsEnabled)
}

export function createEmptyCustomerDetails(): CustomerDetails {
  return {
    name: '',
    email: '',
    phone: '',
    company: '',
    address: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      countryOrRegion: '',
    },
    notes: '',
    customQuestionAnswers: [],
    smsOptIn: false,
  }
}
